'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Calendar,
  Card,
  Collapse,
  ConfigProvider,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Col,
  Segmented,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import esES from 'antd/locale/es_ES';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import {
  CheckCircleOutlined,
  FileTextOutlined,
  LeftOutlined,
  LoadingOutlined,
  MailOutlined,
  ReloadOutlined,
  RightOutlined,
  SaveOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';

import {
  ORDER_TYPES,
  COUNTRY_CODES,
  COST_CENTERS,
  DEFAULT_WAREHOUSE_BY_ORDER_TYPE,
  getOrdersByProject,
  getOrderByActionId,
  getOrderItems,
  createOrderToCreate,
  addItemToOrder,
  updateOrderToCreate,
  updateOrderItem,
  deleteOrderItem,
  uploadSingleOrder,
  OrderResponse,
  type PurchaseOrderRead,
} from '@/services/OrderAssemblyService';
import PurchaseOrderImport from './PurchaseOrderImport';
import { executeProjectAction, fetchProjectInfo } from '@/services/ProjectService';
import {
  enrichOrderReferencesWithSiesaDescriptions as enrichPendingOrderDescriptions,
  enrichOrderWithSiesaDetail,
  getCurrentUserSellerId,
} from '@/services/PendingOrdersService';
import PendingOrdersPDFModal from '@/components/PendingOrdersPDFModal';
import type { SimplifiedPendingOrder } from '@/services/PendingOrdersService';

// ⬇️ Componentes que te compartiré a continuación, pero ya dejo los imports listos.
//    Están en la MISMA RUTA (mismo directorio).
import ModernItemsSection from './ModernItemSection';
import SearchSiesaRow from './SearchSiesaRow';
import {
  LocationSelect,
  WarehouseSelect,
  useWarehouseCatalog,
  warehouseNeedsLocation,
} from './WarehouseFields';
import { SelectedExtension } from './order-types';

// ⬇️ Utilidades y UI helpers (también vendrán luego)
import { currency, ensureGlobalStyles } from './order-helpers';
import styles from './OrderAssembly.module.css';

// Fallbacks locales sugeridos
const MEASURE_UNITS = [
  { value: 'UND', label: 'Unidad' },
  { value: 'ML', label: 'Metro Lineal' },
  { value: 'KG', label: 'Kilogramo' },
];

const { Text } = Typography;
const { Option } = Select;

export interface OrderAssemblyPanelProps {
  visible: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  fkProjectActionId?: number | null;
  actionOrders?: any[];
  actionDone?: boolean;
  embedded?: boolean;
}

// Tipo local de fila en tabla de items
export interface OrderItemUI {
  key: string;
  reference: string;
  description?: string;
  /** Referencia "corta"/externa de SIESA (f120_descripcion_corta). */
  short_reference?: string;
  extension: string;
  warehouse: string;
  /** Ubicación dentro de la bodega (solo bodegas multi-ubicación). */
  location?: string | null;
  price_list?: string;
  measure_unit: string;
  quantity: number;
  unit_price: number;
  notes?: string;
  total: number;
}

export const OrderAssemblyPanel: React.FC<OrderAssemblyPanelProps> = ({
  visible,
  onClose,
  projectId,
  projectName,
  fkProjectActionId,
  actionOrders,
  actionDone,
  embedded = false,
}) => {
  // --- Estado raíz / orquestación
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [deliveryDateModalOpen, setDeliveryDateModalOpen] = useState(false);
  const [deliveryDateDraft, setDeliveryDateDraft] = useState<any>(null);
  const [editForm] = Form.useForm();
  const [watchedReference, setWatchedReference] = useState<any>(undefined);
  const watchedDeliveryDate = Form.useWatch('deliveryDate', form);
  const watchedPurchaseOrder = Form.useWatch('purchaseOrder', form);
  const watchedCountryCode = Form.useWatch('countryCode', form);
  const watchedCostCenter = Form.useWatch('costCenter', form);
  // Bodega dentro del modal de edición: decide si hay que pedir ubicación.
  const editWarehouse = Form.useWatch('warehouse', editForm);

  useEffect(() => {
    try {
      dayjs.locale('es');
    } catch {
      // no-op
    }
  }, []);

  useEffect(() => {
    if (!deliveryDateModalOpen) return;
    try {
      const cur = form.getFieldValue('deliveryDate');
      setDeliveryDateDraft(cur || dayjs().add(1, 'day'));
    } catch {
      setDeliveryDateDraft(dayjs().add(1, 'day'));
    }
  }, [deliveryDateModalOpen, form]);

  const referenceInputRef = useRef<any>(null);

  useEffect(() => {
    try {
      setWatchedReference(itemForm.getFieldValue?.('reference'));
    } catch {
      setWatchedReference(undefined);
    }
  }, [itemForm]);

  const contractSiesaResults = Boolean(watchedReference && String(watchedReference).trim() !== '');

  // Inject style to hide the visible reference input (keeps form model intact)
  useEffect(() => {
    const id = 'oa-hide-reference-style';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      
      [data-oa-reference="1"] { display: none !important; }
    `;
    document.head.appendChild(s);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  const [orderItems, setOrderItems] = useState<OrderItemUI[]>([]);
  // Orden de compra del cliente leída del PDF: llena el encabezado y luego las líneas.
  const [ocRead, setOcRead] = useState<PurchaseOrderRead | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<OrderItemUI | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  // Bodega con la que salen las líneas nuevas del pedido. Arranca con la del
  // tipo (CPT/CPV/CPE) y se puede cambiar buscándola en el maestro de SIESA.
  const [defaultWarehouse, setDefaultWarehouse] = useState<string | null>(null);
  const [warehouseEdited, setWarehouseEdited] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  // Use null to represent 'no selection' explicitly and keep Segmented controlled
  const [selectedOrderType, setSelectedOrderType] = useState<string | null>(null);

  const [existingOrders, setExistingOrders] = useState<OrderResponse[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [isUsingExisting, setIsUsingExisting] = useState(false);
  const [isActionDone, setIsActionDone] = useState<boolean>(!!actionDone);
  const [costCenterEdited, setCostCenterEdited] = useState(false);
  const [formVersion, setFormVersion] = useState(0); // bump to persist snapshots on form edits
  const [projectClientName, setProjectClientName] = useState<string | undefined>(undefined);
  const [projectClientNit, setProjectClientNit] = useState<string | undefined>(undefined);

  const resumeStorageKey = useMemo(() => {
    const id = Number(fkProjectActionId);
    if (!Number.isFinite(id) || id <= 0) return null;
    return `orderAssembly:resume:${id}`;
  }, [fkProjectActionId]);

  // UI helpers
  const debugMode =
    typeof window !== 'undefined' && localStorage.getItem('orderAssemblyDebug') === '1';
  const [isMobile, setIsMobile] = useState(false);
  // Maestro de bodegas del ERP: alimenta el selector del paso de información y
  // el de la edición de una línea del carrito.
  const warehouseCatalog = useWarehouseCatalog();
  const [highlightedRows, setHighlightedRows] = useState<Set<string>>(new Set());
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalOrder, setEmailModalOrder] = useState<SimplifiedPendingOrder | null>(null);
  const [uploading, setUploading] = useState(false);
  const emailOrderRef = useRef<SimplifiedPendingOrder | null>(null);
  const refreshExistingOrdersRef = useRef<() => Promise<void>>(async () => {});
  const openEmailModalRef = useRef<
    (
      overrideOrder?: SimplifiedPendingOrder | null,
      options?: { skipUploadedCheck?: boolean },
    ) => Promise<void>
  >(async () => {});

  // ----- Efectos base
  useEffect(() => {
    ensureGlobalStyles();
  }, []);

  // Fetch project info to resolve client name and NIT for PDF
  useEffect(() => {
    if (!visible || !projectId) return;
    (async () => {
      try {
        const info = await fetchProjectInfo(projectId);
        if (info) {
          setProjectClientName(info.meta?.clientName || undefined);
          setProjectClientNit(info.meta?.clientNit ? String(info.meta.clientNit) : undefined);
        }
      } catch {
        // non-critical – the PDF will fallback to projectName
      }
    })();
  }, [visible, projectId]);

  useEffect(() => {
    const update = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // País por defecto (Colombia=169)
  useEffect(() => {
    if (!visible) return;
    const cur = itemForm.getFieldValue?.('countryCode');
    if (!cur) itemForm.setFieldsValue({ countryCode: '169' });
  }, [visible]);

  // Initialize selectedExtensions
  useEffect(() => {
    itemForm.setFieldsValue({ selectedExtensions: "[]" });
  }, []);

  // Clear highlights when reference changes
  useEffect(() => {
    const subscription = itemForm.getFieldValue?.('reference');
    if (subscription !== undefined) {
      setHighlightedRows(new Set());
    }
  }, [itemForm.getFieldValue?.('reference')]);

  // Helpers: robust mapping/formatting for confirmation step
  const mapCountryLabel = (code: any) => {
    const c = String(code ?? '');
    const found = COUNTRY_CODES.find((cc) => String(cc.value) === c);
    return found ? found.label : (c || '—');
  };

  const mapCostCenterLabel = (code: any) => {
    const c = String(code ?? '');
    const found = COST_CENTERS.find((cc) => String(cc.value) === c);
    return found ? found.label : (c || '—');
  };

  const formatDelivery = (val: any) => {
    if (!val) return '—';
    try {
      if (val && typeof val.format === 'function') return val.format('YYYY-MM-DD');
      if (typeof val === 'string') return val;
      if (val instanceof Date) return dayjs(val).format('YYYY-MM-DD');
      return String(val);
    } catch {
      return String(val);
    }
  };

  // Cost center automático según tipo (si usuario no lo tocó y no es pedido existente)
  useEffect(() => {
    if (!selectedOrderType || isUsingExisting || costCenterEdited) return;
    const defaultCost =
      selectedOrderType === 'CPT' ? '050102' : ['CPV', 'CPE'].includes(selectedOrderType) ? '060102' : undefined;
    if (defaultCost) form.setFieldsValue({ costCenter: defaultCost });
  }, [selectedOrderType, isUsingExisting, costCenterEdited, form]);

  // Bodega por defecto según tipo, con la misma regla que el centro de costo:
  // se propone, y deja de proponerse en cuanto el usuario escoge otra.
  useEffect(() => {
    if (!selectedOrderType || warehouseEdited) return;
    const suggested = DEFAULT_WAREHOUSE_BY_ORDER_TYPE[selectedOrderType];
    if (!suggested) return;
    setDefaultWarehouse(suggested);
    form.setFieldsValue({ orderWarehouse: suggested });
  }, [selectedOrderType, warehouseEdited, form]);

  // Al retomar un pedido ya montado, la bodega que manda es la de sus líneas.
  useEffect(() => {
    if (!isUsingExisting || warehouseEdited) return;
    const fromItems = orderItems.find((it) => String(it.warehouse || '').trim())?.warehouse;
    if (!fromItems) return;
    setDefaultWarehouse(String(fromItems));
    form.setFieldsValue({ orderWarehouse: String(fromItems) });
  }, [isUsingExisting, orderItems, warehouseEdited, form]);

  // Cargar pedidos existentes del proyecto
  const loadExisting = async () => {
    if (!projectId) return;
    setLoadingExisting(true);
    try {
      const list = await getOrdersByProject(projectId);
      setExistingOrders(list || []);

      // Si no tenemos fkProjectActionId, auto-seleccionar el pedido más reciente para continuar
      if ((!fkProjectActionId || !Number(fkProjectActionId)) && Array.isArray(list) && list.length > 0) {
        const sorted = [...list].sort((a: any, b: any) => {
          const aDate = new Date(a.updated_at || a.created_at || a.createdAt || a.date || 0).getTime();
          const bDate = new Date(b.updated_at || b.created_at || b.createdAt || b.date || 0).getTime();
          return bDate - aDate;
        });
        const pick = sorted[0];
        if (pick) {
          setOrderId(Number(pick.id));
          setSelectedOrderType(pick.order_type ? String(pick.order_type) : null);
          form.setFieldsValue({
            deliveryDate: pick.delivery_date ? dayjs(pick.delivery_date) : undefined,
            purchaseOrder: pick.purchase_order,
            countryCode: pick.country_code,
            costCenter: pick.cost_center,
            observations: pick.notes,
          });
          const items = await getOrderItems(pick.id);
          setOrderItems(
            (items || []).map((i: any) => ({
              key: String(
                i.id ??
                  (typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random()}`),
              ),
              reference: i.reference,
              description: i.description || i.item_description || i.item_short_description || undefined,
              short_reference: i.short_reference || undefined,
              extension: i.extension,
              warehouse: i.warehouse,
              location: i.location || null,
              price_list: i.price_list,
              measure_unit: i.measure_unit,
              quantity: Number(i.quantity) || 0,
              unit_price: Number(i.unit_price) || 0,
              notes: i.notes,
              total: (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
            })),
          );
          setIsUsingExisting(true);
          setCurrentStep(1);
        }
      }
    } catch {
      setExistingOrders([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  // Inicialización al abrir
  useEffect(() => {
    if (!visible) return;
    (async () => {
      let resume: any = null;
      try {
        if (resumeStorageKey && typeof window !== 'undefined') {
          const raw = window.localStorage.getItem(resumeStorageKey);
          if (raw) resume = JSON.parse(raw);
        }
      } catch {
        resume = null;
      }

      form.resetFields();
      itemForm.resetFields();
      setOrderItems([]);
      setOcRead(null);
      setCurrentStep(0);
      setOrderId(null);
      setSelectedOrderType(null);
      setIsUsingExisting(false);
      form.setFieldsValue({ countryCode: '169' });

      // Re-hidratar desde el resume local (si existe), aun antes de ir al backend
      if (resume) {
        const resumeOrderId = Number(resume?.orderId);
        const resumeStep = Number(resume?.currentStep);
        const resumeType = resume?.selectedOrderType ?? null;
        if (Number.isFinite(resumeStep) && resumeStep >= 0) {
          setCurrentStep(Math.max(0, Math.min(2, resumeStep)));
        }
        if (resumeType !== undefined) {
          setSelectedOrderType(resumeType === null ? null : String(resumeType));
        }
        if (Number.isFinite(resumeOrderId) && resumeOrderId > 0) {
          setOrderId(resumeOrderId);
        }
        if (resume?.formValues) {
          const fv = { ...resume.formValues };
          if (fv.deliveryDate) {
            fv.deliveryDate = dayjs(fv.deliveryDate);
          }
          form.setFieldsValue(fv);
        }
        if (Array.isArray(resume?.items)) {
          setOrderItems(
            resume.items.map((i: any) => ({
              ...i,
              key: String(
                i.key ??
                  (typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random()}`),
              ),
            })),
          );
        }
      }

      if (fkProjectActionId) {
        try {
          setLoadingExisting(true);
          const order = await getOrderByActionId(Number(fkProjectActionId));
          const list = order ? [order] : [];
          setExistingOrders(list);

          if (list.length > 0) {
            const resumeOrderId = Number(resume?.orderId);
            const resumeStep = Number(resume?.currentStep);
            if (Number.isFinite(resumeStep) && resumeStep >= 0) {
              setCurrentStep(Math.max(0, Math.min(2, resumeStep)));
            }

            const matched = list.find(
              (o: any) =>
                Number(o.fk_project_action) === Number(fkProjectActionId) ||
                Number(o.id) === Number(fkProjectActionId),
            );

            const toUse =
              Number.isFinite(resumeOrderId) && resumeOrderId > 0
                ? list.find((o: any) => Number(o.id) === resumeOrderId) || matched
                : matched;

            if (toUse) {
              setOrderId(toUse.id);
              setSelectedOrderType(toUse.order_type ? String(toUse.order_type) : null);
              setIsUsingExisting(true);
              form.setFieldsValue({
                deliveryDate: toUse.delivery_date ? dayjs(toUse.delivery_date) : undefined,
                purchaseOrder: toUse.purchase_order,
                countryCode: toUse.country_code,
                costCenter: toUse.cost_center,
                observations: toUse.notes,
              });
              const items = await getOrderItems(toUse.id);
              setOrderItems(
                (items || []).map((i: any) => ({
                  key: String(i.id),
                  reference: i.reference,
                  description: i.description || i.item_description || i.item_short_description || undefined,
                  short_reference: i.short_reference || undefined,
                  extension: i.extension,
                  warehouse: i.warehouse,
                  location: i.location || null,
                  price_list: i.price_list,
                  measure_unit: i.measure_unit,
                  quantity: Number(i.quantity) || 0,
                  unit_price: Number(i.unit_price) || 0,
                  notes: i.notes,
                  total: (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
                })),
              );
            }
          } else if (Array.isArray(actionOrders) && actionOrders.length > 0) {
            // espejo de actionOrders si back aún no persiste
            setExistingOrders(
              actionOrders.map((o: any) => ({
                id: o.order_id || o.id,
                // Use undefined when order_type is not provided so the UI selector stays unselected
                order_type: o.order_type || undefined,
                fk_project: projectId,
                fk_project_action: fkProjectActionId!,
                country_code: o.country_code || '',
                delivery_date: o.delivery_date ?? undefined,
                cost_center: o.cost_center ?? undefined,
                notes: o.notes ?? undefined,
                purchase_order: o.document ?? undefined,
                created_at: o.createdAt ?? undefined,
                updated_at: o.updatedAt ?? undefined,
                upload: !!o.upload,
                ready: !!o.ready,
              })),
            );
          } else if (projectId) {
            await loadExisting();
          }
        } catch {
          await loadExisting();
        } finally {
          setLoadingExisting(false);
        }
        return;
      }

      // caso general (sin acción)
      await loadExisting();
    })();
  }, [visible, resumeStorageKey, fkProjectActionId, projectId, actionOrders, form, itemForm]);

  useEffect(() => {
    if (!visible) return;
    if (!resumeStorageKey) return;
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(
        resumeStorageKey,
        JSON.stringify({ orderId, currentStep }),
      );
    } catch {
      // no-op
    }
  }, [visible, resumeStorageKey, orderId, currentStep]);

  // ---- Derivados
  const { consecutive: derivedConsecutive, isSubido } = useMemo(() => {
    let consecutive: string | null = null;
    let uploaded = false;
    try {
      const findCurrent = () => {
        if (orderId && existingOrders.length) {
          const found = existingOrders.find((o: any) => Number(o.id) === Number(orderId));
          if (found) return found;
        }
        if (Array.isArray(actionOrders) && actionOrders.length) return actionOrders[0];
        if (existingOrders.length) return existingOrders[0];
        return null;
      };
      const current = findCurrent();
      if (current) {
        const documentCandidate =
          current.document ??
          current.order_number ??
          current.consecutive ??
          null;
        uploaded =
          Boolean(current.upload || (current as any).uploaded) ||
          Boolean(String(documentCandidate || '').trim());

        if (current.consecutive) consecutive = String(current.consecutive);
        const raw =
          current.document ??
          current.order_number ??
          null;
        if (!consecutive && raw) {
          const s = String(raw);
          if (s.includes('-')) {
            const parts = s.split('-');
            if (parts.length > 1) consecutive = parts.slice(1).join('-');
          } else if (s.includes('/')) {
            const parts = s.split('/');
            if (parts.length > 1) consecutive = parts.slice(1).join('/');
          } else {
            const m = s.match(/(\d{2,})/);
            if (m) consecutive = m[1];
          }
        }
      }
    } catch {
      consecutive = null;
      uploaded = false;
    }
    return { consecutive, isSubido: uploaded };
  }, [orderId, existingOrders, actionOrders]);

  const currentOrderRecord = useMemo(() => {
    try {
      if (orderId && existingOrders.length) {
        return existingOrders.find((o: any) => Number(o.id) === Number(orderId)) || null;
      }
      return null;
    } catch {
      return null;
    }
  }, [orderId, existingOrders]);

  const hasUploadedOrderNumber = useMemo(() => {
    const raw =
      currentOrderRecord?.order_number ??
      (currentOrderRecord as any)?.document ??
      currentOrderRecord?.consecutive ??
      null;
    return Boolean(String(raw || '').trim());
  }, [currentOrderRecord]);

  const isOrderReady = useMemo(() => {
    if (!currentOrderRecord || !hasUploadedOrderNumber) return false;
    return Boolean(
      currentOrderRecord.ready ||
        (currentOrderRecord as any).is_ready ||
        currentOrderRecord.upload ||
        (currentOrderRecord as any).uploaded,
    );
  }, [currentOrderRecord, hasUploadedOrderNumber]);

  const isEditingLocked = useMemo(
    () => Boolean(isOrderReady || (isActionDone && hasUploadedOrderNumber)),
    [isActionDone, isOrderReady, hasUploadedOrderNumber],
  );

  // ---- Acciones
  const handleCreateOrder = async () => {
    if (isEditingLocked) return message.warning('No permitido: la acción está completada');
    if (!selectedOrderType) {
      return message.error('Debe seleccionar un tipo de pedido (CPT / CPV / CPE) antes de crear el pedido');
    }
    try {
      setLoading(true);
      const values = await form.validateFields();

      const MIN_DATE = dayjs().add(1, 'day').startOf('day');
      const rawDelivery = values.deliveryDate;
      const delivery = rawDelivery ? dayjs(rawDelivery).format('YYYY-MM-DD') : undefined;

  if (!delivery) return message.error('La fecha de entrega es requerida');
      if (dayjs(delivery).isBefore(MIN_DATE, 'day')) {
        return message.error(`La fecha de entrega debe ser >= ${MIN_DATE.format('YYYY-MM-DD')}`);
      }
      const payload: any = {
        order_type: selectedOrderType,
        fk_project_action: fkProjectActionId || undefined,
        country_code: values.countryCode,
        ...(delivery ? { delivery_date: delivery } : {}),
        cost_center: values.costCenter,
        notes: values.observations ?? undefined,
        purchase_order: values.purchaseOrder,
      };
      if (!payload.fk_project_action) {
        message.error('Este panel debe abrirse desde una acción de tipo PEDIDO (fk_project_action)');
        setLoading(false);
        return;
      }

      const response: any = await createOrderToCreate(payload);
      const newId = Number(response?.id ?? response?.order_id ?? response?.data?.id ?? 0);
      if (Number.isFinite(newId) && newId > 0) {
        setOrderId(newId);
        setCurrentStep(1);
        message.success('Pedido creado');
      } else {
        message.error((response as any)?.message || 'Error al crear el pedido');
      }
    } catch (err: any) {
      message.error(err?.data?.message || err?.message || 'Error al crear el pedido');
    } finally {
      setLoading(false);
    }
  };

  // Persistir un snapshot ligero (paso, orderId, tipo y formulario) para restaurar tras recarga
  useEffect(() => {
    if (!visible) return;
    if (!resumeStorageKey) return;
    const timer = window.setTimeout(() => {
      try {
        const fv = form.getFieldsValue?.(true) || {};
        const serializedForm = {
          ...fv,
          deliveryDate: fv.deliveryDate ? dayjs(fv.deliveryDate).format('YYYY-MM-DD') : null,
        };
        const snapshot = {
          orderId,
          currentStep,
          selectedOrderType,
          formValues: serializedForm,
          items: orderItems,
        };
        window.localStorage.setItem(resumeStorageKey, JSON.stringify(snapshot));
      } catch (err) {
        console.warn('No se pudo persistir el snapshot de montaje', err);
      }
    }, 200);
    return () => window.clearTimeout(timer);
  }, [
    visible,
    resumeStorageKey,
    orderId,
    currentStep,
    selectedOrderType,
    orderItems,
    form,
    formVersion,
  ]);

  const handleAddItem = async () => {
    if (isEditingLocked) return message.warning('No permitido: la acción está completada');
    if (!orderId) return message.error('Primero debe crear el pedido');

    try {
      // validamos pero si falla leemos snapshot (para no perder “notes”)
      const values = await itemForm.validateFields().catch(() => itemForm.getFieldsValue(true));
      const snapshot = itemForm.getFieldsValue?.(true) ?? {};
      const notes =
        values?.notes ?? snapshot?.notes ?? itemForm.getFieldValue?.('notes') ?? undefined;
      const itemDescription =
        values?.item_description ??
        values?.item_short_description ??
        snapshot?.item_description ??
        snapshot?.item_short_description ??
        itemForm.getFieldValue?.('item_description') ??
        itemForm.getFieldValue?.('item_short_description') ??
        undefined;
      // Referencia "corta"/externa del ítem en SIESA: la que reconoce el
      // cliente. Va aparte de la descripción porque el documento del pedido
      // muestra las dos.
      const itemShortReference =
        values?.item_short_reference ??
        snapshot?.item_short_reference ??
        itemForm.getFieldValue?.('item_short_reference') ??
        undefined;

      const selectedExtensions: SelectedExtension[] = (() => {
        try {
          const val = itemForm.getFieldValue("selectedExtensions");
          return Array.isArray(val) ? val : (typeof val === 'string' ? JSON.parse(val) : []);
        } catch (e) {
          console.error('Error parsing selectedExtensions:', e);
          return [];
        }
      })();
      if (selectedExtensions.length === 0) {
        return message.error('Debe seleccionar al menos una extensión');
      }
      // ensure all chosen extensions have a valid quantity
      const bad = selectedExtensions.some(se => se.quantity === undefined || se.quantity === null || se.quantity <= 0);
      if (bad) {
        return message.error('Asegúrate de ingresar una cantidad válida (>0) para cada extensión');
      }

      const unitPrice =
        values.unit_price === undefined || values.unit_price === null
          ? undefined
          : parseFloat(String(values.unit_price));

      // Check for duplicate items (same reference + extension)
      const existingItems = orderItems;
      const duplicates: string[] = [];
      const duplicateKeys = new Set<string>();
      for (const selExt of selectedExtensions) {
        const existingItem = existingItems.find(item =>
          item.reference === values.reference && item.extension === selExt.extension
        );
        if (existingItem) {
          duplicates.push(selExt.extension);
          duplicateKeys.add(existingItem.key);
        }
      }
      if (duplicates.length > 0) {
        setHighlightedRows(duplicateKeys);
        return message.error(
          `Ya existe la extensión de: ${duplicates.join(', ')} en el ítem ${values.reference}. ` +
            'Edítalo desde el carrito con el botón "Editar" para cambiar cantidad, precio o bodega.',
        );
      }

      // Agregar múltiples ítems, uno por cada extensión seleccionada
      const newItems: OrderItemUI[] = [];
      for (const selExt of selectedExtensions) {
        const payload = {
          fk_order_to_create: orderId,
          reference: values.reference,
          description: typeof itemDescription === 'string' ? itemDescription.trim() : itemDescription,
          short_reference:
            typeof itemShortReference === 'string' ? itemShortReference.trim() : itemShortReference,
          extension: selExt.extension,
          warehouse: values.warehouse,
          location: values.location || undefined,
          price_list: values.price_list || undefined,
          measure_unit: values.measure_unit,
          quantity: selExt.quantity,
          unit_price: unitPrice,
          notes: typeof notes === 'string' ? notes.trim() : notes,
        };

        const response = await addItemToOrder(payload as any);
        if (response?.id) {
          const q = Number(selExt.quantity) || 0;
          const up = Number(unitPrice) || 0;
          newItems.push({
            key: String(response.id),
            reference: payload.reference,
            description:
              response.description ||
              response.item_description ||
              response.item_short_description ||
              payload.description,
            short_reference: response.short_reference || payload.short_reference,
            extension: payload.extension,
            warehouse: payload.warehouse,
            location: payload.location || null,
            price_list: payload.price_list,
            measure_unit: payload.measure_unit,
            quantity: q,
            unit_price: up,
            notes: payload.notes,
            total: q * up,
          });
        } else {
          message.error(`Error al agregar el artículo para extensión ${selExt.extension}`);
          return;
        }
      }

      setOrderItems((prev) => [...prev, ...newItems]);
      // Reset the entire item form to allow searching for new items
      itemForm.resetFields();
      setHighlightedRows(new Set());
      message.success(`${newItems.length} artículo(s) agregado(s)`);
    } catch (e: any) {
      const backendMsg = e?.response?.data?.message || e?.data?.message;
      message.error(backendMsg || e?.message || 'Error al agregar el artículo');
    }
  };

  /**
   * Abre la edición de una línea del carrito. Antes solo se podía eliminar y
   * volver a agregar: equivocarse en una cantidad costaba rehacer el ítem.
   */
  function handleEditItem(record: OrderItemUI) {
    if (isEditingLocked) {
      return message.warning('No permitido: la acción está completada');
    }
    setEditingItem(record);
    editForm.setFieldsValue({
      warehouse: record.warehouse,
      location: record.location || undefined,
      measure_unit: record.measure_unit,
      quantity: record.quantity,
      unit_price: record.unit_price,
      notes: record.notes,
    });
    setEditModalVisible(true);
  }

  const closeEditItem = () => {
    setEditModalVisible(false);
    setEditingItem(null);
    editForm.resetFields();
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    if (isEditingLocked) {
      return message.warning('No permitido: acción completada o pedido finalizado');
    }
    try {
      const values = await editForm.validateFields();
      const quantity =
        values.quantity === undefined || values.quantity === null
          ? undefined
          : parseFloat(String(values.quantity));
      const unitPrice =
        values.unit_price === undefined || values.unit_price === null
          ? undefined
          : parseFloat(String(values.unit_price));
      // La ubicación solo se guarda si la bodega elegida la admite; si no, se
      // manda vacía para no dejar colgada la de la bodega anterior.
      const location = warehouseNeedsLocation(values.warehouse, warehouseCatalog.byCode)
        ? values.location || ''
        : '';

      const patched = (it: OrderItemUI): OrderItemUI => ({
        ...it,
        warehouse: values.warehouse,
        location: location || null,
        measure_unit: values.measure_unit,
        quantity: Number(quantity) || 0,
        unit_price: Number(unitPrice) || 0,
        notes: values.notes ?? undefined,
        total: (Number(quantity) || 0) * (Number(unitPrice) || 0),
      });

      // Una línea que todavía no llegó al backend (sin id numérico) se corrige
      // solo en pantalla: no hay nada que actualizar en el servidor.
      const itemId = Number(editingItem.key);
      if (!Number.isFinite(itemId) || itemId <= 0) {
        setOrderItems((prev) =>
          prev.map((it) => (String(it.key) === String(editingItem.key) ? patched(it) : it)),
        );
        message.success('Artículo actualizado');
        closeEditItem();
        return;
      }

      setLoading(true);
      const payload: any = {
        fk_order_to_create: Number(orderId),
        reference: editingItem.reference,
        description: editingItem.description ?? undefined,
        short_reference: editingItem.short_reference ?? undefined,
        extension: editingItem.extension,
        warehouse: values.warehouse,
        location,
        price_list: editingItem.price_list || undefined,
        measure_unit: values.measure_unit,
        quantity,
        unit_price: unitPrice,
        notes: values.notes ?? undefined,
      };
      const res = await updateOrderItem(itemId, payload);
      if (res?.id) {
        message.success('Artículo actualizado');
        setOrderItems((prev) =>
          prev.map((it) =>
            String(it.key) === String(res.id)
              ? {
                  ...it,
                  reference: res.reference,
                  description:
                    res.description ||
                    res.item_description ||
                    res.item_short_description ||
                    it.description,
                  short_reference: res.short_reference || it.short_reference,
                  extension: res.extension,
                  warehouse: res.warehouse,
                  location: res.location || null,
                  price_list: res.price_list,
                  measure_unit: res.measure_unit,
                  quantity: Number(res.quantity),
                  unit_price: Number(res.unit_price),
                  notes: res.notes,
                  total: Number(res.quantity) * Number(res.unit_price),
                }
              : it,
          ),
        );
        closeEditItem();
      } else {
        message.error('Error actualizando artículo');
      }
    } catch (err: any) {
      // El backend rechaza el precio fuera del rango de la lista: ese mensaje es
      // el útil, no un "error al actualizar" genérico.
      const backendMsg = err?.data?.message || err?.response?.data?.message;
      if (backendMsg) message.error(backendMsg);
      else if (!err?.errorFields) message.error(err?.message || 'Error actualizando artículo');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async (): Promise<boolean> => {
    if (isEditingLocked) {
      message.warning('No permitido: acción completada o pedido finalizado');
      return false;
    }
    if (!orderId) {
      message.error('No hay un pedido seleccionado para actualizar');
      return false;
    }
    try {
      setLoading(true);
      const values = await form.validateFields();
      const rawDelivery = values.deliveryDate;
      const delivery = rawDelivery
        ? rawDelivery.format
          ? rawDelivery.format('YYYY-MM-DD')
          : rawDelivery
        : undefined;

      const payload: any = {
        order_type: selectedOrderType,
        fk_project_action: fkProjectActionId || undefined,
        country_code: values.countryCode,
        // Only include delivery_date when a value was provided. This lets
        // updates proceed without a delivery date while keeping the field
        // required for new orders via form validation.
        ...(delivery ? { delivery_date: delivery } : {}),
        cost_center: values.costCenter,
        notes: values.observations ?? undefined,
        purchase_order: values.purchaseOrder,
      };
      const response = await updateOrderToCreate(orderId, payload);
      if (response?.id) {
        message.success('Pedido actualizado');
        setExistingOrders((prev) =>
          prev.map((o) => (Number(o.id) === Number(response.id) ? response : o)),
        );
        form.setFieldsValue({
          deliveryDate: response.delivery_date ? dayjs(response.delivery_date) : undefined,
          purchaseOrder: response.purchase_order,
          countryCode: response.country_code,
          costCenter: response.cost_center,
          observations: response.notes,
        });
        return true;
      } else {
        message.error((response as any)?.message || 'Error actualizando pedido');
        return false;
      }
    } catch (err: any) {
      message.error(err?.data?.message || err?.message || 'Error actualizando pedido');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    if (isEditingLocked) {
      return message.warning('No permitido: pedido finalizado o acción completada');
    }
    if (!orderId) return message.error('No hay un pedido seleccionado');
    if (orderItems.length === 0) return message.error('El pedido debe tener ítems');

    setLoading(true);
    updateOrderToCreate(orderId, { ready: true })
      .then(async () => {
        message.success('Pedido marcado como listo');
        if (fkProjectActionId) {
          try {
            await executeProjectAction(Number(fkProjectActionId), {
              mode: 'done',
              effective_at: dayjs().toISOString(),
              reason: 'Pedido marcado como listo',
            } as any);
            setIsActionDone(true);
            message.success('Acción marcada como ejecutada');
            
            // Automáticamente subir el pedido a SIESA después de marcar como ejecutado
            try {
              setUploading(true);
              const res = await uploadSingleOrder(orderId);
              const uploadedDocumentNumber = String(res?.order_number || '').trim();
              if (uploadedDocumentNumber) {
                message.success(`Pedido subido a SIESA automáticamente: ${res.order_number}`);
              } else {
                message.success('Pedido subido a SIESA automáticamente');
              }
              // Refrescar órdenes existentes para actualizar el estado isSubido
              await refreshExistingOrdersRef.current();
              await openEmailModalRef.current(
                emailOrderRef.current
                  ? {
                      ...emailOrderRef.current,
                      orderNumber: uploadedDocumentNumber || emailOrderRef.current.orderNumber,
                      document_number: uploadedDocumentNumber || emailOrderRef.current.document_number,
                      document_status: 'SUBIDO_SIESA',
                      status: 'SUBIDO_SIESA',
                    }
                  : null,
                { skipUploadedCheck: true },
              );
              
              // Cerrar el drawer después de un pequeño delay para que el usuario vea el estado final
            } catch (uploadErr: any) {
              const uploadMsg = uploadErr?.data?.message || uploadErr?.message || 'Error al subir pedido a SIESA';
              message.error(uploadMsg);
              // Cerrar igualmente después del error
            } finally {
              setUploading(false);
            }
            
            try {
              window?.dispatchEvent?.(
                new CustomEvent('projectsUpdated', { detail: { projectId } }),
              );
            } catch {}
          } catch (err2: any) {
            message.error(err2?.data?.message || 'Error al actualizar acción');
            // Cerrar después del error
          }
        }
      })
      .catch((err: any) => {
        message.error(err?.data?.message || 'Error al actualizar pedido');
      })
      .finally(() => setLoading(false));
  };

  async function handleRemoveItem(key: string) {
    if (isEditingLocked) {
      return message.warning('No permitido: la acción está completada');
    }

    // If there's no orderId yet, this is still a local-only item — just remove from UI
    if (!orderId) {
      setOrderItems((prev) => prev.filter((it) => it.key !== key));
      return message.success('Artículo eliminado (solo UI)');
    }

    // If the key looks like a numeric server id, attempt to delete from backend
    const numericId = Number(key);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      // Non-numeric keys are treated as unsaved; remove locally
      setOrderItems((prev) => prev.filter((it) => it.key !== key));
      return message.success('Artículo eliminado (solo UI)');
    }

    // Confirm delete via API
    setLoading(true);
    try {
      await deleteOrderItem(numericId);
      // Remove from UI after backend deletion
      setOrderItems((prev) => prev.filter((it) => it.key !== key));
      message.success('Artículo eliminado');
    } catch (err: any) {
      console.error('Error eliminando item:', err);
      message.error(err?.data?.message || err?.message || 'Error eliminando el artículo');
    } finally {
      setLoading(false);
    }
  }

  // ---- Columnas para tabla de ítems (se usan en ModernItemsSection y confirmación)
  const columns = [
    { title: 'Referencia', dataIndex: 'reference', key: 'reference', width: 180, ellipsis: true },
    { title: 'Ext', dataIndex: 'extension', key: 'extension', width: 120, ellipsis: true },
    {
      title: 'Bodega',
      dataIndex: 'warehouse',
      key: 'warehouse',
      width: 130,
      ellipsis: true,
      render: (warehouse: string, record: OrderItemUI) =>
        record.location ? (
          <Tooltip title={`Ubicación ${record.location}`}>
            <span>
              {warehouse}
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                {record.location}
              </Text>
            </span>
          </Tooltip>
        ) : (
          warehouse
        ),
    },
    { title: 'Lista', dataIndex: 'price_list', key: 'price_list', width: 90, align: 'center' as const },
    { title: 'UM', dataIndex: 'measure_unit', key: 'measure_unit', width: 80, align: 'center' as const },
    { title: 'Cantidad', dataIndex: 'quantity', key: 'quantity', width: 95, align: 'right' as const },
    {
      title: 'P.Unit',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right' as const,
      render: (v: number) => currency(v),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 135,
      align: 'right' as const,
      render: (v: number) => currency(v),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: any, record: OrderItemUI) => (
        <Space size={0}>
          <Button
            type="text"
            size="small"
            disabled={isEditingLocked}
            onClick={() => handleEditItem(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar?"
            onConfirm={() => handleRemoveItem(record.key)}
            disabled={isEditingLocked}
          >
            <Button type="text" danger size="small" disabled={isEditingLocked}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getTotalAmount = () =>
    orderItems.reduce(
      (sum, item) => sum + (Number.isFinite(Number(item.total)) ? Number(item.total) : 0),
      0,
    );

  const emailOrder = useMemo<SimplifiedPendingOrder | null>(() => {
    if (!orderId && orderItems.length === 0) return null;

    const formValues = form.getFieldsValue?.(true) || {};
    const deliveryDateRaw = formValues?.deliveryDate;
    const deliveryDate = deliveryDateRaw ? dayjs(deliveryDateRaw).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    const documentCore = String(derivedConsecutive || orderId || '').trim();
    const orderTypeCode = String(selectedOrderType || '').trim().toUpperCase();
    const documentNumber = documentCore
      ? orderTypeCode
        ? `${orderTypeCode}-${documentCore}`
        : documentCore
      : String(orderId || '');

    let sellerId = '';
    let sellerName = '';
    try {
      const currentSellerId = getCurrentUserSellerId();
      if (currentSellerId) sellerId = String(currentSellerId).trim();
      const sessionRaw = localStorage.getItem('session');
      if (sessionRaw) {
        const session = JSON.parse(sessionRaw);
        // session may be { user: {...} } or the user object itself
        const su = (session && (session.user || session.user === 0) ? session.user : session) || {};

        // seller_id for SIESA should prefer cedula/document identifiers over CRM internal ids
        const possibleId =
          su?.cedula ??
          su?.seller_id ??
          su?.sellerId ??
          su?.document ??
          su?.document_number ??
          su?.identification ??
          session?.cedula ??
          session?.seller_id ??
          session?.sellerId ??
          session?.document ??
          session?.document_number ??
          session?.identification;
        const possibleName = su?.name ?? su?.user ?? session?.user ?? session?.name ?? su?.email ?? '';

        if (!sellerId) sellerId = String(possibleId ?? '').trim();
        sellerName = String(possibleName ?? '').trim();
      }
    } catch (e) {
      // ignore local storage parse errors
    }

    const referencesByKey = new Map<string, any>();
    let totalQuantity = 0;
    let totalValue = 0;

    orderItems.forEach((item) => {
      const reference = String(item.reference || 'SIN-REFERENCIA').trim();
      const itemDescription = String(item.description || '').trim();
      const itemShortReference = String(item.short_reference || '').trim();
      const measureUnit = String(item.measure_unit || 'UND').trim() || 'UND';
      const extension = String(item.extension || '').trim();
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unit_price) || 0;
      const subtotal = Number.isFinite(Number(item.total)) ? Number(item.total) : quantity * unitPrice;
      const key = `${reference}::${measureUnit}`;

      if (!referencesByKey.has(key)) {
        // Prefer any existing server-provided reference description when available
        let refDescription = itemDescription || String(item.notes || '').trim();
        try {
          const matchedBackend = existingOrders && Array.isArray(existingOrders) && orderId
            ? existingOrders.find((o: any) => Number(o.id) === Number(orderId))
            : null;
          if (matchedBackend && Array.isArray((matchedBackend as any).references)) {
            const foundRef = (matchedBackend as any).references.find((r: any) => String(r.reference || '').trim() === reference);
            if (foundRef) {
              refDescription = String(foundRef.item_short_description || foundRef.description || refDescription).trim();
            }
          }
        } catch (e) {
          // ignore
        }

        referencesByKey.set(key, {
          reference,
          description: refDescription,
          item_short_description: refDescription,
          short_reference: itemShortReference || undefined,
          measure_unit: measureUnit,
          items: [],
        });
      }

      referencesByKey.get(key).items.push({
        item_description: itemDescription || undefined,
        item_short_description: itemDescription || undefined,
        description: itemDescription || undefined,
        short_reference: itemShortReference || undefined,
        item_extension: extension,
        quantity_ordered: quantity,
        quantity_pending: quantity,
        quantity_invoiced: 0,
        stock_quantity: 0,
        unit_price: unitPrice,
        subtotal_value: subtotal,
        gross_value: subtotal,
        average_cost_per_unit: 0,
        standard_cost: 0,
        average_cost: 0,
      });

      totalQuantity += quantity;
      totalValue += subtotal;
    });

    const references = Array.from(referencesByKey.values());
    // If we have a backend existing order, prefer its totals (tax, discount, total)
    let backendSubtotal = totalValue;
    let backendDiscount = 0;
    let backendTax = 0;
    let backendTotal = totalValue;
    try {
      const matched = existingOrders && Array.isArray(existingOrders) && orderId
        ? existingOrders.find((o: any) => Number(o.id) === Number(orderId))
        : null;
      if (matched) {
        backendSubtotal = (matched.document_subtotal_value !== undefined && matched.document_subtotal_value !== null)
          ? matched.document_subtotal_value
          : backendSubtotal;
        backendDiscount = (matched.document_discount_value !== undefined && matched.document_discount_value !== null)
          ? matched.document_discount_value
          : backendDiscount;
        backendTax = (matched.document_tax_value !== undefined && matched.document_tax_value !== null)
          ? matched.document_tax_value
          : backendTax;
        backendTotal = (matched.document_total_value !== undefined && matched.document_total_value !== null)
          ? matched.document_total_value
          : backendTotal;
      }
    } catch (e) {}

    const statusLabel = isSubido ? 'SUBIDO_SIESA' : isOrderReady ? 'PENDIENTE_SIESA' : 'PENDIENTE_MONTAJE';
    const purchaseOrder = String(formValues?.purchaseOrder || '').trim();
    const observations = String(formValues?.observations || '').trim();

    return {
      id: String(orderId || documentNumber || projectId),
      orderNumber: documentNumber || String(orderId || projectId),
      document_number: documentNumber || String(orderId || projectId),
      purchase_order: purchaseOrder,
      client: String(projectId || ''),
      client_name: projectClientName || projectName || 'Cliente',
      client_nit: projectClientNit || undefined,
      client_branch: undefined,
      branch: undefined,
      branch_name: undefined,
      contact: undefined,
      seller: sellerId,
      seller_name: sellerName,
      document_type: 'Pedido',
      document_date: dayjs().format('YYYY-MM-DD'),
      creation_date: dayjs().format('YYYY-MM-DD'),
      delivery_date: deliveryDate,
      due_date: deliveryDate,
      document_status: statusLabel,
      status: statusLabel,
      observations,
      warehouse_name: orderItems[0]?.warehouse || undefined,
      document_subtotal_value: backendSubtotal,
      document_discount_value: backendDiscount,
      document_tax_value: backendTax,
      document_total_value: backendTotal,
    total_items: orderItems.length,
    total_quantity: totalQuantity,
    total_value: backendTotal,
    references,
  };
  }, [derivedConsecutive, form, formVersion, isOrderReady, isSubido, orderId, orderItems, projectId, projectName, projectClientName, projectClientNit, selectedOrderType]);

  useEffect(() => {
    emailOrderRef.current = emailOrder;
  }, [emailOrder]);

  const canSendUploadedEmail = Boolean(
    emailOrder &&
      orderItems.length > 0 &&
      isSubido &&
      selectedOrderType &&
      derivedConsecutive,
  );

  const emailOrderList = useMemo(() => (emailOrder ? [emailOrder] : []), [emailOrder]);

  function closeEmailModal() {
    setEmailModalOpen(false);
    setEmailModalOrder(null);
  }

  async function openEmailModal(
    overrideOrder?: SimplifiedPendingOrder | null,
    options?: { skipUploadedCheck?: boolean },
  ) {
    const baseOrder = overrideOrder || emailOrder;
    if (!baseOrder || orderItems.length === 0) {
      message.warning('Agrega ítems al pedido antes de enviarlo por correo');
      return;
    }
    if (!options?.skipUploadedCheck && !isSubido) {
      message.warning('El correo solo está disponible cuando el pedido ya fue subido a SIESA');
      return;
    }

    setEmailModalOrder(baseOrder);
    setEmailModalOpen(true);

    try {
      const sellerIdHint = String(getCurrentUserSellerId() || '').trim() || undefined;
      const enrichedOrder = await enrichOrderWithSiesaDetail(baseOrder, {
        forceSync: true,
        sellerIdHint,
      });
      const enrichedWithDescriptions = await enrichPendingOrderDescriptions(enrichedOrder);
      setEmailModalOrder({
        ...enrichedWithDescriptions,
        observations:
          enrichedWithDescriptions.observations ||
          enrichedWithDescriptions.notes ||
          baseOrder.observations ||
          undefined,
        notes:
          enrichedWithDescriptions.notes ||
          enrichedWithDescriptions.observations ||
          baseOrder.notes ||
          undefined,
      });
    } catch (error) {
      console.error('[OrderAssemblyPanel] Error enriching order for email modal:', error);
    }
  }

  const handleOpenEmailModal = () => {
    if (!emailOrder || orderItems.length === 0) {
      message.warning('Agrega ítems al pedido antes de enviarlo por correo');
      return;
    }
    void openEmailModal();
  };

  // ---- UI auxiliares
  const stepDefs = [
    { title: 'Base', full: 'Base del pedido', icon: <FileTextOutlined />, desc: 'Datos base' },
    { title: 'Items', full: 'Items', icon: <ShoppingCartOutlined />, desc: 'Agregar productos' },
    { title: 'Revision', full: 'Revision', icon: <CheckCircleOutlined />, desc: 'Revisar y cerrar' },
  ];

  const renderOrderTypeSelector = () => (
    <div style={{ marginBottom: 8 }}>
      <Segmented
        className="oa-type-segment"
        block
        options={ORDER_TYPES.map((t) => ({
          label: (
            <Tooltip title={t.label}>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>{t.value}</span>
                <span className="oa-mini-label">{t.label.split(' ').slice(0, 2).join(' ')}</span>
              </span>
            </Tooltip>
          ),
          value: t.value,
        }))}
        // when undefined, Segmented shows no active selection
  // Force Segmented to be controlled. Passing null keeps it with no active selection.
  // Cast to any to satisfy Segmented's typed prop while keeping runtime behavior clear.
  value={selectedOrderType as any}
        onChange={(val) => {
          setCostCenterEdited(false);
          // Cambiar de tipo vuelve a proponer su bodega, igual que el centro de
          // costo: son las dos cosas que cuelgan del tipo de documento.
          setWarehouseEdited(false);
          setSelectedOrderType(val as string);
        }}
        disabled={isUsingExisting || !!isOrderReady}
      />
    </div>
  );

  const renderInfoStep = () => (
    <>
      {!fkProjectActionId && existingOrders.length > 0 && (
        <Collapse
          bordered={false}
          size="small"
          className="oa-collapse-ghost"
          defaultActiveKey={['existing']}
          collapsible="header"
          items={[
            {
              key: 'existing',
              label: (
                <Space size={6}>
                  <Text strong style={{ fontSize: 13 }}>
                    Pedidos existentes ({existingOrders.length})
                  </Text>
                  {isUsingExisting && <Badge status="processing" />}
                </Space>
              ),
              children: (
                <Card
                  size="small"
                  className="existing-orders-table"
                  style={{ marginBottom: 12, borderRadius: 8 }}
                  styles={{ body: { padding: loadingExisting ? 12 : 0 } }}
                  extra={
                    <Tooltip title="Refrescar lista">
                      <Button
                        size="small"
                        onClick={loadExisting}
                        loading={loadingExisting}
                        icon={<ReloadOutlined />}
                      />
                    </Tooltip>
                  }
                >
                  {loadingExisting ? (
                    <Skeleton active paragraph={{ rows: 3 }} />
                  ) : (
                    <Table
                      size="small"
                      tableLayout="fixed"
                      rowKey="id"
                      pagination={{ pageSize: 5, showSizeChanger: false }}
                      dataSource={existingOrders}
                      onRow={(record) => ({
                        onClick: async () => {
                          if (fkProjectActionId) return;
                            if (isUsingExisting && orderId === record.id) {
                            setIsUsingExisting(false);
                            setOrderId(null);
                            setOrderItems([]);
                            form.resetFields();
                            form.setFieldsValue({ countryCode: '169' });
                              setSelectedOrderType(null);
                            message.info('Pedido deseleccionado');
                          } else {
                            setOrderId(record.id);
                            setSelectedOrderType(record.order_type);
                            form.setFieldsValue({
                              deliveryDate: dayjs(record.delivery_date),
                              purchaseOrder: record.purchase_order,
                              countryCode: record.country_code,
                              costCenter: record.cost_center,
                              observations: record.notes,
                            });
                            const items = await getOrderItems(record.id);
                            setOrderItems(
                              (items || []).map((i: any) => ({
                                key: String(i.id),
                                reference: i.reference,
                                description: i.description || i.item_description || i.item_short_description || undefined,
                                short_reference: i.short_reference || undefined,
                                extension: i.extension,
                                warehouse: i.warehouse,
                                location: i.location || null,
                                price_list: i.price_list,
                                measure_unit: i.measure_unit,
                                quantity: Number(i.quantity) || 0,
                                unit_price: Number(i.unit_price) || 0,
                                notes: i.notes,
                                total: (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
                              })),
                            );
                            setIsUsingExisting(true);
                            message.success('Pedido cargado');
                          }
                        },
                      })}
                      rowClassName={(record) =>
                        isUsingExisting && orderId === record.id ? 'existing-order-selected' : ''
                      }
                      columns={[
                        { title: 'ID', dataIndex: 'id', width: 55 },
                        { title: 'Tipo', dataIndex: 'order_type', width: 70 },
                        { title: 'Entrega', dataIndex: 'delivery_date', width: 90 },
                        { title: 'OC', dataIndex: 'purchase_order', width: 90 },
                        { title: 'Centro Costo', dataIndex: 'cost_center', width: 100 },
                        {
                          title: 'Ítems',
                          width: 60,
                          render: (_, r: any) => (orderId === r.id ? orderItems.length : '—'),
                        },
                      ]}
                      locale={{ emptyText: 'Sin pedidos previos' }}
                    />
                  )}
                </Card>
              ),
            },
          ]}
        />
      )}

      {fkProjectActionId && !isUsingExisting && !orderId && (
        <PurchaseOrderImport
          fkProjectActionId={fkProjectActionId}
          result={ocRead}
          disabled={isEditingLocked}
          onItemsAdded={() => {}}
          onResult={(r) => {
            setOcRead(r);
            // La OC VMI trae la fecha del día; el CRM exige entrega desde mañana.
            const min = dayjs().add(1, 'day').startOf('day');
            const ocDate = r.defaults.delivery_date ? dayjs(r.defaults.delivery_date) : min;
            if (!selectedOrderType) setSelectedOrderType('CPV');
            form.setFieldsValue({
              purchaseOrder: r.defaults.purchase_order || undefined,
              deliveryDate: ocDate.isBefore(min) ? min : ocDate,
            });
            setFormVersion((v) => v + 1);
          }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        // Disable entire Info form while the user hasn't chosen CPT/CPV/CPE
        disabled={isEditingLocked || !selectedOrderType}
        className="oa-compact oa-form-surface"
        onValuesChange={() => setFormVersion((v) => v + 1)}
      >
        {renderOrderTypeSelector()}

        <Row gutter={8}>
          <Col span={12}>
              <Form.Item
                name="deliveryDate"
                label="Fecha Entrega"
                rules={[
                  // Required only when creating a new order (not when editing an existing one)
                  { required: !isUsingExisting, message: 'La fecha de entrega es requerida' },
                  () => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const min = dayjs().add(1, 'day').startOf('day');
                      return value.isBefore(min)
                        ? Promise.reject(`Debe ser >= ${min.format('YYYY-MM-DD')}`)
                        : Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  readOnly
                  placeholder="Selecciona fecha (mín: mañana)"
                  value={(() => {
                    const v = form.getFieldValue('deliveryDate');
                    return v ? dayjs(v).format('YYYY-MM-DD') : '';
                  })()}
                  onClick={() => setDeliveryDateModalOpen(true)}
                />
              </Form.Item>
            </Col>
          <Col span={12}>
            <Form.Item
              name="purchaseOrder"
              label="Orden de Compra"
              rules={[{ required: true, message: 'La orden de compra es requerida' }]}
            >
              <Input placeholder="OC / PO" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={8}>
          <Col xs={24} md={12}>
            <Form.Item
              name="countryCode"
              label="País"
              rules={[{ required: true, message: 'El país es requerido' }]}
            >
              <Select
                showSearch
                placeholder="Seleccione el país"
                style={{ width: '100%' }}
                allowClear
                optionFilterProp="children"
              >
                {COUNTRY_CODES.map((c) => (
                  <Option key={c.value} value={c.value}>
                    {c.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="costCenter"
              label="Centro de Costo"
              rules={[{ required: true, message: 'El centro de costo es requerido' }]}
            >
              <Select
                showSearch
                placeholder="Seleccione el centro de costo"
                style={{ width: '100%' }}
                allowClear
                optionFilterProp="children"
                onChange={() => setCostCenterEdited(true)}
              >
                {COST_CENTERS.map((center) => (
                  <Option key={center.value} value={center.value}>
                    {center.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={8}>
          <Col xs={24} md={12}>
            <Form.Item
              name="orderWarehouse"
              label="Bodega del pedido"
              extra={
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {selectedOrderType
                    ? `Sugerida para ${selectedOrderType}. Cámbiala y todas las líneas nuevas saldrán de ahí; cada línea se puede ajustar en el carrito.`
                    : 'Escoge primero el tipo de pedido.'}
                </Text>
              }
            >
              <WarehouseSelect
                warehouses={warehouseCatalog.warehouses}
                loading={warehouseCatalog.loading}
                disabled={isEditingLocked || !selectedOrderType}
                onChange={(next) => {
                  setWarehouseEdited(true);
                  setDefaultWarehouse(next || null);
                  form.setFieldsValue({ orderWarehouse: next });
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="observations" label="Notas" id="observations">
          <Input.TextArea
            rows={5}
            placeholder="Observaciones del pedido"
            maxLength={400}
            style={{ width: '100%', resize: 'vertical', padding: '6px 11px', minHeight: 90 }}
            disabled={isEditingLocked}
            title={
              isEditingLocked
                ? 'No se pueden modificar las notas de un pedido finalizado / acción completada'
                : undefined
            }
          />
        </Form.Item>
      </Form>
    </>
  );

  const renderItemsStep = () => (
    <>
      {ocRead && orderId && (
        <PurchaseOrderImport
          fkProjectActionId={fkProjectActionId}
          orderId={orderId}
          result={ocRead}
          disabled={isEditingLocked}
          onResult={setOcRead}
          onItemsAdded={(items) => setOrderItems((prev) => [...prev, ...items])}
        />
      )}
      <div style={{ marginBottom: 8 }}>
        <SearchSiesaRow
          form={itemForm}
          referenceInputRef={referenceInputRef}
          defaultWarehouse={defaultWarehouse}
          contractResults={contractSiesaResults}
          disabled={!selectedOrderType}
          selectedOrderType={selectedOrderType}
        />
      </div>

      <ModernItemsSection
        form={itemForm}
        onAdd={handleAddItem}
        items={orderItems}
        columns={columns}
        total={getTotalAmount()}
        measureUnits={MEASURE_UNITS}
        onRemove={handleRemoveItem}
        onEdit={handleEditItem}
        loadingAdd={false}
        stacked={isMobile}
        isActionDone={isEditingLocked}
        orderReady={isOrderReady}
        referenceInputRef={referenceInputRef}
        disabled={!selectedOrderType}
        highlightedRows={highlightedRows}
        tabsLayout={!isMobile}
        selectedOrderType={selectedOrderType}
      />
    </>
  );

  const renderConfirmStep = () => {
    const confirmDeliveryDate = watchedDeliveryDate ?? currentOrderRecord?.delivery_date ?? undefined;
    const confirmPurchaseOrder =
      watchedPurchaseOrder ??
      currentOrderRecord?.purchase_order ??
      (currentOrderRecord as any)?.document ??
      undefined;
    const confirmCountryCode = watchedCountryCode ?? currentOrderRecord?.country_code ?? undefined;
    const confirmCostCenter = watchedCostCenter ?? currentOrderRecord?.cost_center ?? undefined;
    const themeColor =
      selectedOrderType === 'CPT'
        ? '#1677ff'
        : selectedOrderType === 'CPV'
        ? '#eb2f96'
        : selectedOrderType === 'CPE'
        ? '#52c41a'
        : '#1677ff';
    const consecutive = derivedConsecutive;
    const statusText = isSubido
      ? 'Subido a SIESA'
      : hasUploadedOrderNumber
      ? 'Listo para SIESA'
      : 'En montaje';
    const metaCards = [
      { label: 'Proyecto', value: projectName || '—' },
      { label: 'Entrega', value: formatDelivery(confirmDeliveryDate) || '—' },
      { label: 'Orden de compra', value: String(confirmPurchaseOrder || '—') },
      { label: 'País', value: mapCountryLabel(confirmCountryCode) || '—' },
      { label: 'Centro de costo', value: mapCostCenterLabel(confirmCostCenter) || '—' },
      { label: 'Estado', value: statusText },
    ];

    return (
      <div
        style={{
          marginBottom: 16,
          padding: 18,
          borderRadius: 14,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.65), rgba(245,245,245,0.45))',
          border: `1px solid rgba(255,255,255,0.6)`,
          boxShadow: `0 10px 30px rgba(16,24,40,0.06)`,
          backdropFilter: 'blur(8px) saturate(120%)',
          WebkitBackdropFilter: 'blur(8px) saturate(120%)',
          display: 'flex',
          gap: 16,
          alignItems: 'stretch',
          flexWrap: 'wrap',
          overflowX: 'hidden',
          maxWidth: '100%',
        }}
        className="oa-glass-summary"
      >
        <div style={{ flex: '0 0 120px', display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${themeColor}, rgba(255,255,255,0.08))`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 800,
              fontSize: 20,
              boxShadow: `0 8px 24px ${themeColor}33`,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div>{selectedOrderType || '—'}</div>
              <div style={{ fontSize: 12, opacity: 0.95 }}>
                {consecutive ? `#${consecutive}` : hasUploadedOrderNumber ? 'PENDIENTE SIESA' : 'PENDIENTE'}
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', display: 'grid', gap: 12 }}>
          <div
            style={{
              display: 'grid',
              gap: 8,
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            }}
          >
            {metaCards.map((card) => (
              <div
                key={card.label}
                style={{
                  border: '1px solid #e6e8ec',
                  borderRadius: 8,
                  padding: '8px 10px',
                  background: '#ffffffb8',
                  minWidth: 0,
                }}
              >
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <Card size="small" styles={{ body: { padding: isMobile ? 8 : 14 } }} style={{ borderRadius: 10, background: '#f5f5f7' }}>
              <div style={{ background: '#f5f5f7', padding: isMobile ? 8 : 16, borderRadius: 6 }}>
                <Table
                  size="small"
                  tableLayout="fixed"
                  columns={isMobile 
                    ? [
                        { title: 'Producto', dataIndex: 'reference', key: 'reference', ellipsis: true },
                        { title: 'Ext', dataIndex: 'extension', key: 'extension', width: 50 },
                        { title: 'Cant', dataIndex: 'quantity', key: 'quantity', width: 60, align: 'right' as const },
                        { 
                          title: 'Total', 
                          dataIndex: 'total', 
                          key: 'total', 
                          width: 80, 
                          align: 'right' as const,
                          render: (v: number) => currency(v)
                        },
                        {
                          title: '',
                          key: 'actions',
                          width: 62,
                          render: (_: any, record: OrderItemUI) => (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                              disabled={isEditingLocked}
                              onClick={() => handleEditItem(record)}
                            >
                              Editar
                            </Button>
                          ),
                        },
                      ]
                    // La revisión también deja corregir: es donde uno cae en
                    // cuenta de la cantidad mal digitada, y volver al carrito
                    // para eso era el paso de más que sobraba.
                    : columns
                  }
                  dataSource={orderItems}
                  pagination={false}
                  locale={{ emptyText: 'No hay artículos' }}
                  rowKey={(r: any) => r.key}
                  rowClassName={(record) => highlightedRows.has(record.key) ? 'highlighted-row' : ''}
                  scroll={{ x: isMobile ? 620 : 1120 }}
                                      style={isMobile ? { fontSize: '12px' } : { fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }}>Subtotal</div>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>{currency(getTotalAmount())}</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to refresh existing orders from the backend
  async function refreshExistingOrders() {
    try {
      let freshOrders: any[] = [];
      if (fkProjectActionId) {
        const fromAction = await getOrderByActionId(Number(fkProjectActionId));
        freshOrders = fromAction ? [fromAction] : [];
      } else {
        freshOrders = await getOrdersByProject(projectId);
      }
      setExistingOrders(
        (freshOrders || []).map((o: any) => ({
          id: o.id,
          order_type: o.order_type || undefined,
          fk_project: projectId,
          fk_project_action: o.fk_project_action,
          country_code: o.country_code || '',
          delivery_date: o.delivery_date ?? undefined,
          cost_center: o.cost_center ?? undefined,
          notes: o.notes ?? undefined,
          purchase_order: o.purchase_order ?? undefined,
          created_at: o.createdAt ?? undefined,
          updated_at: o.updatedAt ?? undefined,
          upload: !!o.upload,
          ready: !!o.ready,
          order_number: o.order_number ?? undefined,
          consecutive: o.order_number ?? undefined,
        })),
      );
    } catch (err) {
      // Silently fail on refresh
    }
  }

  useEffect(() => {
    refreshExistingOrdersRef.current = refreshExistingOrders;
    openEmailModalRef.current = openEmailModal;
  });

  const renderFooter = () => (
    <div style={{ textAlign: 'right' }}>
      <Space>
        {debugMode && (
          <>
            <Button size="small" type="dashed" onClick={() => setCurrentStep(0)}>Debug: Info</Button>
          </>
        )}

        {currentStep > 0 && <Button onClick={() => setCurrentStep(currentStep - 1)}>Anterior</Button>}

        {currentStep === 0 && !isUsingExisting && (
          <Button
            type="primary"
            onClick={handleCreateOrder}
            loading={loading}
            disabled={!selectedOrderType || isEditingLocked}
          >
            Iniciar montaje
          </Button>
        )}

        {currentStep === 0 && isUsingExisting && (
          <Button
            type="primary"
            onClick={async () => {
              const ok = await handleUpdateOrder();
              if (ok) setCurrentStep(1);
            }}
            loading={loading}
            disabled={!orderId || isEditingLocked}
          >
            Guardar base
          </Button>
        )}

        {currentStep === 1 && (
          <Button
            type="primary"
            onClick={() => setCurrentStep(2)}
            disabled={orderItems.length === 0 || isEditingLocked}
          >
            Ir a revision
          </Button>
        )}

        {currentStep === 2 && !isOrderReady && (
          <Tooltip title="Se enviará el pedido y se subirá automáticamente a SIESA">
            <Button type="primary" icon={<SaveOutlined />} onClick={handleFinish} disabled={isEditingLocked}>
              Cerrar montaje
            </Button>
          </Tooltip>
        )}

        {currentStep === 2 && (
          <Button
            icon={<MailOutlined />}
            onClick={handleOpenEmailModal}
            disabled={!canSendUploadedEmail || uploading}
          >
            Enviar por correo
          </Button>
        )}

        {uploading && !isSubido && (
          <Tag icon={<LoadingOutlined />} color="processing" style={{ fontSize: 13, padding: '4px 12px', margin: 0, fontWeight: 600 }}>
            Procesando y subiendo a SIESA...
          </Tag>
        )}

        {isSubido && !uploading && (
          <Tag color="success" style={{ fontSize: 13, padding: '4px 12px', margin: 0, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircleOutlined /> COMPLETADO — #{derivedConsecutive}
          </Tag>
        )}
      </Space>
    </div>
  );

  const allowed = debugMode ? 2 : orderId ? (orderItems.length > 0 ? 2 : 1) : 0;
  const assemblyStatusLabel = isSubido
    ? `Subido${derivedConsecutive ? ` #${derivedConsecutive}` : ''}`
    : isOrderReady
      ? 'Listo para subir'
      : orderId
        ? 'Montaje en curso'
        : 'Nuevo montaje';
  const stepOptions = stepDefs.map((s, i) => ({
    label: (
      <Tooltip title={s.desc}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {s.icon}
          <span>{s.title}</span>
        </span>
      </Tooltip>
    ),
    value: i,
    disabled: !debugMode && i > allowed,
  }));

  return (
    <ConfigProvider locale={esES} getPopupContainer={() => document.body}>
    <Drawer
      className={`${isMobile ? styles['oab-drawer-full'] : ''} order-assembly-theme-${selectedOrderType || 'CPT'}`}
      title={
        <div id="order-assembly-title" className="order-assembly-topbar">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>Montaje comercial</div>
            <Text type="secondary">{projectName} - {assemblyStatusLabel}</Text>
          </div>
          <Space>
            <Tooltip title="Cerrar">
              <Button size="small" onClick={onClose}>Cerrar</Button>
            </Tooltip>
          </Space>
        </div>
      }
      placement={isMobile ? 'bottom' : 'right'}
      width={
        isMobile
          ? undefined
          : typeof window !== 'undefined'
              ? (() => {
                  const viewport = window.innerWidth;
                  const maxAllowed = Math.max(720, viewport - 56);
                  const desired = Math.max(Math.round(viewport * 0.72), 860);
                  return Math.min(desired, Math.min(1160, maxAllowed));
                })()
              : 980
      }
      height={isMobile ? '100vh' : undefined}
      aria-labelledby="order-assembly-title"
      zIndex={5000}
      getContainer={embedded ? false : undefined}
      rootStyle={embedded ? { position: 'absolute' } : undefined}
      open={visible}
      onClose={onClose}
      maskClosable={true}
      keyboard={true}
      closable={false}
      styles={{
        content: isMobile ? { height: '100vh', borderRadius: 0 } : undefined,
        mask: { zIndex: 4999 },
        header: { borderBottom: '1px solid #eef2f7' },
        body: {
          padding: isMobile ? 12 : 14,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'hidden',
        },
        footer: { borderTop: '1px solid #f0f0f0', padding: '16px 24px' },
      }}
      footer={renderFooter()}
    >
      <div className={`drawer-gradient-bg order-assembly-wrapper order-assembly-theme-${selectedOrderType || 'CPT'}`} style={{ overflowX: 'hidden', maxWidth: '100%', minHeight: 0 }}>
        {debugMode && (
          <Card size="small" style={{ marginBottom: 12, borderColor: '#faad14' }}>
            <Space size={8} wrap>
              <Text strong style={{ color: '#fa8c16' }}>Modo PRUEBAS:</Text>
              <Button size="small" onClick={() => setCurrentStep(0)}>Info</Button>
              <Button size="small" onClick={() => setCurrentStep(1)}>Artículos</Button>
              <Button size="small" onClick={() => setCurrentStep(2)}>Confirmación</Button>
              <Text type="secondary" style={{ fontSize: 11 }}>Saltos sin validar (solo pruebas)</Text>
            </Space>
          </Card>
        )}

        <Segmented
          className="oa-step-segment"
          size="small"
          value={currentStep}
          onChange={async (v) => {
            const nextStep = Number(v);
            if (!Number.isFinite(nextStep) || nextStep === currentStep) return;
            if (currentStep === 0 && isUsingExisting && nextStep > 0) {
              const ok = await handleUpdateOrder();
              if (!ok) return;
            }
            setCurrentStep(nextStep);
          }}
          options={stepOptions}
        />

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          <Tag bordered={false} color="blue">{assemblyStatusLabel}</Tag>
          <Tag bordered={false}>{selectedOrderType || 'Sin tipo'}</Tag>
          <Tag bordered={false}>{orderItems.length} items</Tag>
          {getTotalAmount() > 0 && <Tag bordered={false} color="green">{currency(getTotalAmount())}</Tag>}
          {orderId ? <Tag bordered={false}>Montaje #{orderId}</Tag> : null}
        </div>

        <div style={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', overflowX: 'hidden', overflowY: 'auto', maxWidth: '100%' }}>
          {currentStep === 0 && renderInfoStep()}
          {currentStep === 1 && renderItemsStep()}
          {currentStep === 2 && renderConfirmStep()}

          <PendingOrdersPDFModal
            visible={emailModalOpen}
            onClose={closeEmailModal}
            order={emailModalOrder}
            orders={emailModalOrder ? [emailModalOrder] : emailOrderList}
            currentIndex={0}
          />

          <Modal
            title="Editar artículo"
            open={editModalVisible}
            onOk={handleSaveEdit}
            onCancel={closeEditItem}
            confirmLoading={loading}
            okText="Guardar cambios"
            cancelText="Cancelar"
            destroyOnHidden
          >
            {/* Referencia, extensión y lista identifican la línea y su precio:
                cambiarlas es otro artículo, no una corrección. Se muestran para
                dar contexto y lo editable es lo que de verdad se equivoca uno. */}
            <div
              style={{
                marginBottom: 14,
                padding: '10px 12px',
                borderRadius: 8,
                background: '#f5f7fa',
                border: '1px solid #e6ebf1',
              }}
            >
              <Text strong style={{ fontSize: 14 }}>{editingItem?.reference}</Text>
              {editingItem?.extension ? (
                <Tag bordered={false} style={{ marginLeft: 8 }}>{editingItem.extension}</Tag>
              ) : null}
              {editingItem?.price_list ? (
                <Tag bordered={false} color="blue">{`Lista ${editingItem.price_list}`}</Tag>
              ) : null}
              {editingItem?.description ? (
                <div style={{ marginTop: 4, color: '#64748b', fontSize: 12 }}>
                  {editingItem.description}
                </div>
              ) : null}
            </div>

            <Form form={editForm} layout="vertical" requiredMark={false}>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="quantity"
                    label="Cantidad"
                    rules={[
                      { required: true, message: 'Ingrese la cantidad' },
                      () => ({
                        validator: (_, value) =>
                          Number(value) > 0
                            ? Promise.resolve()
                            : Promise.reject(new Error('La cantidad debe ser mayor que 0')),
                      }),
                    ]}
                  >
                    <InputNumber min={0} step={1} stringMode style={{ width: '100%' }} autoFocus />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="unit_price"
                    label="Precio unitario"
                    rules={[{ required: true, message: 'Ingrese el precio unitario' }]}
                  >
                    <InputNumber min={0} step={0.01} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="measure_unit" label="Unidad de medida" rules={[{ required: true }]}>
                <Select
                  options={MEASURE_UNITS.map((m) => ({ value: m.value, label: m.label }))}
                />
              </Form.Item>

              <Form.Item name="warehouse" label="Bodega" rules={[{ required: true, message: 'Seleccione bodega' }]}>
                <WarehouseSelect
                  warehouses={warehouseCatalog.warehouses}
                  loading={warehouseCatalog.loading}
                  onChange={(next) => {
                    const values: any = { warehouse: next };
                    if (!warehouseNeedsLocation(next, warehouseCatalog.byCode)) {
                      values.location = undefined;
                    }
                    editForm.setFieldsValue(values);
                  }}
                />
              </Form.Item>

              {warehouseNeedsLocation(editWarehouse, warehouseCatalog.byCode) ? (
                <Form.Item
                  name="location"
                  label="Ubicación"
                  extra={
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Bodega multi-ubicación · opcional, queda registrada en el CRM
                    </Text>
                  }
                >
                  <LocationSelect warehouse={editWarehouse} />
                </Form.Item>
              ) : null}

              <Form.Item name="notes" label="Notas">
                <Input.TextArea rows={4} maxLength={400} />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            open={deliveryDateModalOpen}
            onCancel={() => {
              setDeliveryDateModalOpen(false);
              setDeliveryDateDraft(null);
            }}
            okText="Aceptar"
            cancelText="Cancelar"
            onOk={() => {
              if (!deliveryDateDraft) {
                setDeliveryDateModalOpen(false);
                return;
              }
              form.setFieldsValue({ deliveryDate: deliveryDateDraft });
              setDeliveryDateModalOpen(false);
              setDeliveryDateDraft(null);
            }}
            destroyOnHidden
            zIndex={30000}
            styles={{ body: { paddingTop: 18 } }}
          >
            <Calendar
              fullscreen={false}
              value={deliveryDateDraft || form.getFieldValue('deliveryDate') || dayjs().add(1, 'day')}
              disabledDate={(current) => current && current < dayjs().add(1, 'day').startOf('day')}
              onSelect={(d) => {
                setDeliveryDateDraft(d);
              }}
              onPanelChange={(d) => {
                setDeliveryDateDraft((prev: any) => prev || d);
              }}
              headerRender={({ value, onChange }) => {
                const prevMonth = () => onChange(value.clone().subtract(1, 'month'));
                const nextMonth = () => onChange(value.clone().add(1, 'month'));
                return (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 8px 12px 8px', marginTop: 6 }}>
                    <Button size="small" onClick={prevMonth} icon={<LeftOutlined />} />
                    <div style={{ fontWeight: 700 }}>
                      {value.locale('es').format('MMMM YYYY')}
                    </div>
                    <Button size="small" onClick={nextMonth} icon={<RightOutlined />} />
                  </div>
                );
              }}
            />
          </Modal>
        </div>
      </div>
    </Drawer>
    </ConfigProvider>
  );
};

export default OrderAssemblyPanel;
